import { type AppThunk, type HighlightConfigKey, setHighlightConfigKey } from '@salescore/client-redux'
import dayjs, { type Dayjs } from 'dayjs'
import type { NonEmptyArray } from 'effect/Array'
import { t } from 'i18next'

import { useVmid } from '../components/VmidContext'
import { useViewStateHighlightConfigKey } from './useViewState'

interface HighlightConfig {
  value: HighlightConfigKey
  label: string
  date: Dayjs | undefined
}

// i18n: グローバル変数・配列で保持する系のラベルはi18nがうまく動かないことがありそうなので一旦保留
export const dateForDiffHighlightOptions: NonEmptyArray<HighlightConfig> = [
  {
    value: '',
    label: t(`ハイライトなし`),
    date: undefined,
  },
  {
    value: '1 day',
    label: t(`昨日`),
    date: dayjs().subtract(1, 'day'),
  },
  {
    value: '2 day',
    label: t(`一昨日`),
    date: dayjs().subtract(2, 'day'),
  },
  {
    value: '3 day',
    label: t(`3日前`),
    date: dayjs().subtract(3, 'day'),
  },
  {
    value: '1 week',
    label: t(`1週間`),
    date: dayjs().subtract(1, 'week'),
  },
]

export const highlightConfigToDateString = (key: HighlightConfigKey | undefined): string | undefined =>
  dateForDiffHighlightOptions.find((x) => x.value === key)?.date?.format('YYYY-MM-DD')

export const useHighlightConfig = (
  onUpdate: (key: HighlightConfigKey) => void,
): [HighlightConfigKey, (newKey: HighlightConfigKey) => AppThunk] => {
  const vmid = useVmid()
  const highlightConfigKey = useViewStateHighlightConfigKey()

  const updateHighlightConfigKeyThunk =
    (newKey: HighlightConfigKey): AppThunk =>
    (dispatch, getState) => {
      if (highlightConfigKey === newKey) {
        // 変更がなければ何もしない
        return
      }
      dispatch(setHighlightConfigKey({ vmid, value: newKey }))
      const state = getState()
      const _newKey = state.views.viewStates[vmid]?.highlightConfigKey ?? '' // 反映された最新の値を取得
      if (highlightConfigKey !== _newKey) {
        // dispatch(setHighlightConfigKey({...})) した結果、ストアにも変更が反映されていればonUpdateを呼ぶ
        onUpdate(_newKey)
      }
    }

  return [highlightConfigKey, updateHighlightConfigKeyThunk]
}
